import { TRAINING_CLASS_NAMES } from 'models/constants/training-class-names'
import { ARTICLE_MODEL_CLASS_NAME } from 'models/daos/article/constants'
import { CATEGORY_MODEL_CLASS_NAME } from 'models/daos/category/constants'
import { COMMENT_MODEL_CLASS_NAME } from 'models/daos/comment/constants'
import { MOTIVATION_MODEL_CLASS_NAME } from 'models/daos/motivation/constants'
import { ORDER_MODEL_CLASS_NAME } from 'models/daos/order/constants/index.cjs'
import { PAGE_MODEL_CLASS_NAME } from 'models/daos/page/constants'
import { PRODUCT_MODEL_CLASS_NAME } from 'models/daos/product/constants'
import { PROMO_CODE_MODEL_CLASS_NAME } from 'models/daos/promo-code/constants'
import { USER_MODEL_CLASS_NAME } from 'models/daos/user/constants'
import { formatInstanceBeforeSave } from 'models/formatters/before-save'
import getEndpointFromClassNameModelUtil from 'models/utils/get-endpoint-from-class-name'
import { API_METHOD } from 'modules/api/constants'
import openErrorBannerAction from 'modules/banner/actions/open-error'
import openSuccessBannerAction from 'modules/banner/actions/open-success'
import { removeEditingAction } from 'modules/editing/actions'
import { validateLegacyReduxAction } from 'modules/redux/actions/legacy/validate'
import { REDUX_CLASS_NAME_TO_DOMAIN } from 'modules/redux/constants'
import REDUX_ACTION from 'modules/redux/constants/action'
import saveReduxApi from 'modules/redux/services/save'
import getDomainMapKeyFromPathReduxUtil from 'modules/redux/utils/get-domain-map-key-from-path'
import getIdReduxUtil from 'modules/redux/utils/get-id'
import getPathReduxUtil from 'modules/redux/utils/get-path'
import getPathFromDomainPathReduxUtil from 'modules/redux/utils/get-path-from-domain-map-path'
import { getRelativeUrlReduxUtil } from 'modules/redux/utils/get-url'
import isInstanceReduxUtil from 'modules/redux/utils/is-instance'
import isNewReduxUtil from 'modules/redux/utils/is-new'
import { getDomainPathReduxUtil } from 'modules/redux/utils/legacy/get-domain-path'
import { getMergedDataReduxUtil } from 'modules/redux/utils/legacy/get-merged-data'
import { getUpdatedDataReduxUtil } from 'modules/redux/utils/legacy/get-updated-data'
import { getValidationReduxUtil } from 'modules/redux/utils/legacy/get-validation'
import { updateDataReduxUtil } from 'modules/redux/utils/legacy/update-data'
import { EXERCISE_CLASS_NAME } from 'modules/training/models/exercise/constants'
import pushRouterAction from 'modules/router/actions/push'
import { getObjectUtil, isPlainObjectUtil } from 'packages/src'

/**
 * A list of ClassName that needs a navigation after saving.
 * @type {String[]}
 */
const MODEL_CLASS_NAMES_NAVIGATE_AFTER_SAVE = [
  ARTICLE_MODEL_CLASS_NAME,
  EXERCISE_CLASS_NAME,
  CATEGORY_MODEL_CLASS_NAME,
  MOTIVATION_MODEL_CLASS_NAME,
  PRODUCT_MODEL_CLASS_NAME,
  PROMO_CODE_MODEL_CLASS_NAME,
  PAGE_MODEL_CLASS_NAME
]

/**
 * A list of ClassName that needs a banner to be displayed to user when saved.
 * @type {String[]}
 */
const MODEL_CLASS_NAMES_SHOW_SUCCESS_BANNER = [
  ARTICLE_MODEL_CLASS_NAME,
  CATEGORY_MODEL_CLASS_NAME,
  COMMENT_MODEL_CLASS_NAME,
  MOTIVATION_MODEL_CLASS_NAME,
  ORDER_MODEL_CLASS_NAME,
  PRODUCT_MODEL_CLASS_NAME,
  PAGE_MODEL_CLASS_NAME,
  USER_MODEL_CLASS_NAME
]

/**
 * Marks the instance's status as saving.
 * @param  {string} path
 * @returns {Function}
 */
function saving ({ path }) {
  return {
    type: REDUX_ACTION.SAVE,
    payload: {
      path
    }
  }
}

function saveSuccess ({
  path,
  data,
  associatedPath,
  domainListPath
}) {
  return {
    type: REDUX_ACTION.SAVE_SUCCESS,
    payload: {
      path,
      associatedPath,
      domainListPath,
      data
    }
  }
}

function saveError ({
  path,
  error
}) {
  return {
    type: REDUX_ACTION.SAVE_ERROR,
    payload: {
      path,
      error
    }
  }
}

/**
 * Navigates to the instance's pathname.
 * @private
 * @param  {string} path
 * @param  {Object} data
 * @returns {Function}
 */
function navigateToInstance (path, data) {
  return (dispatch, getState) => {
    const { className } = data
    // Get the instance's path
    const domainPath = getDomainPathReduxUtil(className)
    const domainMapKey = getDomainMapKeyFromPathReduxUtil(domainPath)
    const domainMapValue = data[domainMapKey]
    if (className === CATEGORY_MODEL_CLASS_NAME) {
      const section = path
        .slice(path.indexOf('.') + 1)
        .slice(
          0,
          path.slice(path.indexOf('.') + 1).indexOf('.')
        )
      path = `${domainPath}.${section}.data.${domainMapValue}`
    } else {
      path = `${domainPath}.data.${domainMapValue}`
    }
    // Get the updated instance
    const instance = getObjectUtil(getState(), path)
    // Get the instance's edit url
    // e.g. `/articles/new` => `/articles/nutrition/article-slug/edit`
    // e.g. `/articles/old-path` => `/articles/nutrition/new-path/edit`
    let pathname = getRelativeUrlReduxUtil(
      instance
    )
    if (pathname === '/') {
      pathname += 'edit'
    } else {
      pathname += '/edit'
    }
    // Navigate
    dispatch(
      pushRouterAction(pathname)
    )
  }
}

function saveLegacyReduxAction (params) {
  return async (dispatch, getState) => {
    let {
      path,
      endpoint,
      method,
      data,
      additionalData,
      associatedPath,
      domainListPath,
      remoteUrl
    } = params
    // Validate the instance
    dispatch(
      validateLegacyReduxAction(path)
    )
    // Validate the instance
    let instance = getObjectUtil(getState(), path)
    if (getValidationReduxUtil(instance) !== true) {
      return
    }
    // Mark the instance as saving
    dispatch(
      saving({ path })
    )
    if (!data) {
      // It's a new instance, save everything
      if (isNewReduxUtil(instance)) {
        data = getMergedDataReduxUtil(instance)
      } else {
        // It's an update, save the changed values
        data = getUpdatedDataReduxUtil(instance)
      }
    }
    // Format the data before saving
    const { className } = instance
    data = formatInstanceBeforeSave({
      data,
      className
    })
    // Add additional data if necessary
    if (additionalData) {
      data = {
        ...data,
        ...additionalData
      }
    }
    endpoint = endpoint || getEndpointFromClassNameModelUtil(className)
    method = method || (
      isInstanceReduxUtil(instance) && !isNewReduxUtil(instance)
        ? API_METHOD.PUT
        : API_METHOD.POST
    )
    // TODO make it better
    if (!remoteUrl && TRAINING_CLASS_NAMES.includes(className)) {
      remoteUrl = TRAINING_API_ENDPOINT
    }
    try {
      const response = await saveReduxApi({
        endpoint,
        data,
        method,
        remoteUrl
      })
      // Get the `Instance`'s updated data
      data = {
        ...data,
        ...response.data
      }

      const responseExtraData = response.extraData
      if (
        isPlainObjectUtil(responseExtraData) &&
        Object.keys(responseExtraData).length
      ) {
        Object.keys(responseExtraData).forEach(key => {
          const value = responseExtraData[key]
          if (!value) {
            return
          }
          const dataArr = Array.isArray(value) ? value : [value]
          dataArr.forEach((dataItem) => {
            const domainName = REDUX_CLASS_NAME_TO_DOMAIN[dataItem.className]
            dispatch(
              saveSuccess({
                data: dataItem,
                path: getPathFromDomainPathReduxUtil(domainName, getIdReduxUtil(dataItem))
              })
            )
          })
        })
      }

      data = getMergedDataReduxUtil(instance, data)
      // If the `domainListPath` is `undefined` and the `instance`
      // is already saved, set the `domainListPath` to `null`
      // so it's not re-added to its `DomainList`
      if (typeof domainListPath === 'undefined' && !isNewReduxUtil(instance)) {
        domainListPath = null
      }
      dispatch(
        saveSuccess({
          path,
          data,
          associatedPath,
          domainListPath
        })
      )
      // Remove the instance from the editing domain
      dispatch(
        removeEditingAction(path, { shouldNotDiscard: true })
      )
      // Navigate to the `Instance`'s pathname
      if (MODEL_CLASS_NAMES_NAVIGATE_AFTER_SAVE.includes(className)) {
        dispatch(
          navigateToInstance(path, data)
        )
      }
      // Show a success message
      if (MODEL_CLASS_NAMES_SHOW_SUCCESS_BANNER.includes(className)) {
        const label = (className === USER_MODEL_CLASS_NAME) ? 'account' : className
        dispatch(
          openSuccessBannerAction(
            isNewReduxUtil(instance)
              ? `Your ${label.toLowerCase()} was successfully updated`
              : `Your ${label.toLowerCase()} was successfully created`
          )
        )
      }
      // Return the updated instance
      instance = updateDataReduxUtil(instance, data)
      path = getPathReduxUtil(instance)
      return getObjectUtil(getState(), path)
    } catch (error) {
      dispatch(saveError({
        path,
        error
      }))
      dispatch(
        openErrorBannerAction(error)
      )
      // assume action create catch errors
      throw error
    }
  }
}

export {
  saveLegacyReduxAction
}
